
import React, { useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Megaphone, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";

export default function TeacherAnnouncements() {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const { data: contentItems = [], isLoading } = useQuery({
    queryKey: ['content'],
    queryFn: () => base44.entities.Content.list(),
  });

  const announcements = contentItems.find(c => c.content_key === 'teacher_announcements');

  // Mark announcements as viewed (localStorage so it works with the real backend)
  useEffect(() => {
    if (!user || !announcements?.content_key) return;
    const uid = user.id || user.user_id;
    if (!uid) return;
    localStorage.setItem(`eagle_seen_${announcements.content_key}_${uid}`, new Date().toISOString());
    queryClient.invalidateQueries(['content']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.user_id, announcements?.content_key, announcements?.last_updated]);

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-green-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Teacher Announcements</h1>
            <p className="text-slate-600 mt-1">Important Updates and Information</p>
          </div>
        </div>

        {!announcements || !announcements.content ? (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="w-4 h-4 text-blue-700" />
            <AlertDescription className="text-blue-900">
              No announcements at this time. Check back later!
            </AlertDescription>
          </Alert>
        ) : (
          <Card className="border-none shadow-lg">
            <CardHeader>
              <CardTitle>Current Announcements</CardTitle>
              {announcements.last_updated && (
                <p className="text-xs text-slate-500">
                  Last updated: {format(new Date(announcements.last_updated), 'MMMM d, yyyy h:mm a')}
                </p>
              )}
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none">
                <ReactMarkdown
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" />
                    ),
                  }}
                >
                  {announcements.content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
